import {
  ADD_DIR_SVC_MAPPINGS,
  ADD_DIR_SVC_MAPPINGS_FAIL,
  ADD_DIR_SVC_MAPPINGS_SUCCESS,
  GET_DIR_SVC_MAPPINGS,
  GET_DIR_SVC_MAPPINGS_FAIL,
  GET_DIR_SVC_MAPPINGS_SUCCESS,
  QUERY_DIR_SVC,
  QUERY_DIR_SVC_FAIL,
  QUERY_DIR_SVC_SUCCESS,
  RESET_STATE,
  SET_DESTINATION,
  SET_DIR_SVC_MAPPING,
  SET_QUERY_PENDING,
  SET_QUERY_TYPE,
  SET_SELECTED_NODE,
  SYNC_MAPPINGS,
  SYNC_MAPPINGS_SUCCESS,
  SYNC_MAPPINGS_FAIL,
  UPDATE_DIR_SVC_MAPPING,
  UPDATE_DIR_SVC_MAPPING_FAIL,
  UPDATE_DIR_SVC_MAPPING_SUCCESS,
} from 'state/types/dirSvcTypes';

import { API } from 'util/constants';
import {
  ALL_CATALOGS_GUID,
  QUERY_TYPE_SECURITY_GROUPS,
} from 'util/dirSvcUtils';
import type { DirSvcState } from 'state/types/dirSvcTypes';

const initialState: DirSvcState = {
  destination: ALL_CATALOGS_GUID,
  mappings: [],
  queryIsPending: false,
  queryResults: [],
  queryType: QUERY_TYPE_SECURITY_GROUPS,
  selectedNode: null,
};

function addOrUpdateMapping(
  mapping: DirSvcMapping,
  mappings: Array<DirSvcMapping>
) {
  if (mappings.find(m => m.targetGroupGuid === mapping.targetGroupGuid)) {
    return mappings.map(m =>
      m.targetGroupGuid === mapping.targetGroupGuid ? mapping : m
    );
  } else {
    return [...mappings, mapping];
  }
}

export default function reducer(
  state: DirSvcState = initialState,
  action: Action
) {
  switch (action.type) {
    case GET_DIR_SVC_MAPPINGS_SUCCESS:
      return {
        ...state,
        mappings: action.payload,
      };
    case QUERY_DIR_SVC_SUCCESS:
      let queryResults = action.payload
        .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name))
        .map(item => ({
          id: item.dn,
          state: { expanded: true }, // TODO: need to manage expanded states
          ...item,
        }));
      const selectedNode = state.selectedNode;
      if (selectedNode) {
        const oldQueryResultsCopy = state.queryResults.slice();
        const oldQueryParentNode = oldQueryResultsCopy.find(
          node => node.dn === selectedNode.dn
        ); // TODO: needs to be recursive
        oldQueryParentNode.children = queryResults;
        queryResults = oldQueryResultsCopy;
      }
      return {
        ...state,
        queryResults,
      };
    case SET_DESTINATION:
      return {
        ...state,
        destination: action.payload,
      };
    case SET_DIR_SVC_MAPPING:
      return {
        ...state,
        mappings: addOrUpdateMapping(action.payload, state.mappings),
      };
    case SET_QUERY_PENDING:
      return {
        ...state,
        queryIsPending: action.payload,
      };
    case SET_QUERY_TYPE:
      return {
        ...state,
        queryType: action.payload,
      };
    case SET_SELECTED_NODE:
      return {
        ...state,
        selectedNode: action.payload,
      };
    case RESET_STATE:
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

function queryDirectoryService(
  identitySession: string,
  dirSvcUuid: string,
  searchBase: string,
  searchScope: string,
  query: string
) {
  return {
    types: [QUERY_DIR_SVC, QUERY_DIR_SVC_SUCCESS, QUERY_DIR_SVC_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/directory-service/${dirSvcUuid}/items`, {
        params: {
          session: identitySession,
          searchBase,
          searchFilter: query,
          searchScope,
        },
      }),
  };
}

function getDirectoryServiceMappings(
  identitySession: string,
  dirSvcUuid: string
) {
  return {
    types: [
      GET_DIR_SVC_MAPPINGS,
      GET_DIR_SVC_MAPPINGS_SUCCESS,
      GET_DIR_SVC_MAPPINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/directory-service/${dirSvcUuid}/mappings`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function setDestination(destination: string) {
  return {
    type: SET_DESTINATION,
    payload: destination,
  };
}

function setSelectedNode(node: DirSvcNode) {
  return {
    type: SET_SELECTED_NODE,
    payload: node,
  };
}

function setQueryPending(isPending: boolean) {
  return {
    type: SET_QUERY_PENDING,
    payload: isPending,
  };
}

function setDirectoryServiceQueryType(queryType: Object) {
  return {
    type: SET_QUERY_TYPE,
    payload: queryType,
  };
}

function setMapping(mapping: DirSvcMapping) {
  return {
    type: SET_DIR_SVC_MAPPING,
    payload: mapping,
  };
}

function resetState() {
  return {
    type: RESET_STATE,
  };
}

function syncMappings(identitySession: string, dirSvcUuid: string) {
  return {
    types: [SYNC_MAPPINGS, SYNC_MAPPINGS_SUCCESS, SYNC_MAPPINGS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/directory-service/${dirSvcUuid}/mappings/_sync`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function addMappings(
  identitySession: string,
  dirSvcUuid: string,
  mappings: Array<DirSvcMapping>
) {
  return {
    types: [
      ADD_DIR_SVC_MAPPINGS,
      ADD_DIR_SVC_MAPPINGS_SUCCESS,
      ADD_DIR_SVC_MAPPINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/directory-service/${dirSvcUuid}/mappings`, {
        params: {
          session: identitySession,
        },
        data: mappings,
      }),
  };
}

function updateMapping(
  identitySession: string,
  dirSvcUuid: string,
  mapping: DirSvcMapping
) {
  return {
    types: [
      UPDATE_DIR_SVC_MAPPING,
      UPDATE_DIR_SVC_MAPPING_SUCCESS,
      UPDATE_DIR_SVC_MAPPING_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.put(
        `${API}/directory-service/${dirSvcUuid}/mappings/${mapping.uuid}`,
        {
          params: {
            session: identitySession,
          },
          data: mapping,
        }
      ),
  };
}

const actions = {
  addMappings,
  getDirectoryServiceMappings,
  queryDirectoryService,
  resetState,
  setDestination,
  setDirectoryServiceQueryType,
  setMapping,
  setQueryPending,
  setSelectedNode,
  syncMappings,
  updateMapping,
};

export { actions };



// WEBPACK FOOTER //
// ./src/state/reducers/dirSvcReducer.js